import { RotateCcw, Copy, FileText, FileJson, Sparkles, Target, Clock, X } from 'lucide-react';
import type { MatrixItem } from '../types';

interface ResultsViewProps {
  items: MatrixItem[];
  onReset: () => void;
}

export function ResultsView({ items, onReset }: ResultsViewProps) {
  const quickWins = items.filter((item) => item.quadrant === 'quick-wins');
  const bigBets = items.filter((item) => item.quadrant === 'big-bets');
  const fillIns = items.filter((item) => item.quadrant === 'fill-ins');
  const avoid = items.filter((item) => item.quadrant === 'avoid');

  const getQuadrantInfo = (quadrant: string) => {
    switch (quadrant) {
      case 'quick-wins':
        return {
          title: 'Quick Wins',
          description: 'High Impact / Low Effort',
          icon: Sparkles,
          color: 'green',
        };
      case 'big-bets':
        return {
          title: 'Big Bets',
          description: 'High Impact / High Effort',
          icon: Target,
          color: 'blue',
        };
      case 'fill-ins':
        return {
          title: 'Fill-ins',
          description: 'Low Impact / Low Effort',
          icon: Clock,
          color: 'yellow',
        };
      case 'avoid':
        return {
          title: 'Avoid / Deprioritize',
          description: 'Low Impact / High Effort',
          icon: X,
          color: 'red',
        };
      default:
        return null;
    }
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'green':
        return 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'blue':
        return 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]';
      case 'yellow':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'red':
        return 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]';
      default:
        return 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-pri)]';
    }
  };

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown': {
        content = `# Impact / Effort Matrix Results\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;

        const quadrants = [
          { name: 'Quick Wins', items: quickWins },
          { name: 'Big Bets', items: bigBets },
          { name: 'Fill-ins', items: fillIns },
          { name: 'Avoid', items: avoid },
        ];

        quadrants.forEach((quad) => {
          if (quad.items.length > 0) {
            content += `## ${quad.name}\n\n`;
            quad.items.forEach((item) => {
              content += `- ${item.name} (Impact: ${item.impact}, Effort: ${item.effort})\n`;
            });
            content += '\n';
          }
        });

        filename = `impact-effort-matrix-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      }
      case 'json':
        content = JSON.stringify(items, null, 2);
        filename = `impact-effort-matrix-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'clipboard':
        content = `Quick Wins:\n${quickWins.map((item) => `  - ${item.name}`).join('\n')}\n\nBig Bets:\n${bigBets.map((item) => `  - ${item.name}`).join('\n')}\n\nFill-ins:\n${fillIns.map((item) => `  - ${item.name}`).join('\n')}\n\nAvoid:\n${avoid.map((item) => `  - ${item.name}`).join('\n')}`;
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const renderQuadrant = (items: MatrixItem[], quadrant: string) => {
    const info = getQuadrantInfo(quadrant);
    if (!info) return null;
    const Icon = info.icon;

    return (
      <div key={quadrant} className={`p-4  border-2 ${getColorClasses(info.color)}`}>
        <div className="flex items-center gap-2 mb-3">
          <Icon className="h-6 w-6" />
          <div>
            <h3 className="text-xl font-bold">{info.title}</h3>
            <p className="text-sm opacity-90">{info.description}</p>
          </div>
        </div>
        {items.length > 0 ? (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-[var(--bg-raised)] border border-[var(--rule)]"
              >
                <span className="text-[var(--text-pri)]">{item.name}</span>
                <span className="text-sm text-[var(--text-sec)]">
                  Impact: {item.impact} • Effort: {item.effort}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm opacity-75 italic">No items in this category</p>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Impact / Effort Matrix Results
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Items categorized into four quadrants based on Impact and Effort scores
        </p>

        {/* Matrix Visualization */}
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="text-right text-sm text-[var(--text-sec)] pr-2">High Impact</div>
          <div className="text-sm text-[var(--text-sec)]">Low Impact</div>
        </div>

        {/* Quadrants */}
        <div className="space-y-6 mb-6">
          {renderQuadrant(quickWins, 'quick-wins')}
          {renderQuadrant(bigBets, 'big-bets')}
          {renderQuadrant(fillIns, 'fill-ins')}
          {renderQuadrant(avoid, 'avoid')}
        </div>

        {/* Recommendation */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <h3 className="font-semibold text-[var(--text-sec)] mb-2">Prioritization Recommendation</h3>
          <p className="text-sm text-[var(--text-sec)]">
            <strong>Start with Quick Wins:</strong> These provide high value with low effort. 
            {bigBets.length > 0 && ` Then consider Big Bets for strategic initiatives. `}
            {fillIns.length > 0 && ` Fill-ins can be done when you have spare capacity. `}
            {avoid.length > 0 && ` Avoid items in the "Avoid" quadrant unless there are compelling reasons.`}
          </p>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


